import axios from 'axios';
import { useAuthStore } from '../store';

// REACT_APP_* values are baked in at build time. If REACT_APP_API_BASE_URL is
// missing for a production build, we fall back to the same origin under /api
// (works when frontend and backend share a host with a reverse proxy).
// In development we fall back to localhost:5000 for convenience.
const ENV_API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const DEV_FALLBACK = 'http://localhost:5000/api';
const SAME_ORIGIN_FALLBACK =
  typeof window !== 'undefined' ? `${window.location.origin}/api` : '/api';

const API_BASE_URL =
  ENV_API_BASE_URL ||
  (process.env.NODE_ENV === 'production' ? SAME_ORIGIN_FALLBACK : DEV_FALLBACK);

if (!ENV_API_BASE_URL && process.env.NODE_ENV === 'production') {
  // eslint-disable-next-line no-console
  console.warn(
    '[client] REACT_APP_API_BASE_URL not set at build time - falling back to ' +
    SAME_ORIGIN_FALLBACK
  );
}

// Generous default: dashboard/analytics endpoints can be slow on a cold backend
// (first hit after idle, heavy aggregation). 15s was too tight and surfaced
// "timeout exceeded" even when the server was about to respond.
const API_TIMEOUT_MS = Number(process.env.REACT_APP_API_TIMEOUT_MS) || 30000;

// Transient failures (a request that timed out or a momentary network blip) are
// retried automatically for safe/idempotent requests, so a slow first response
// no longer turns into a hard error page.
const MAX_RETRIES = 2;
const RETRY_BASE_DELAY_MS = 800;

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: API_TIMEOUT_MS,
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Credential endpoints: a 401 here means "wrong password / bad OTP", NOT an
// expired session, so they must never trigger a refresh or the timeout modal.
const AUTH_BYPASS = /\/auth\/(admin-login|login|verify-otp|send-otp|request-otp|register|refresh)/;

// A promise that never settles. We return this from the interceptor when the
// session is unrecoverable, so the calling component's .then/.catch never runs
// and no 401 error is ever surfaced to the UI (no error toasts / error states).
// The global session-timeout modal takes over the screen instead.
const NEVER = () => new Promise(() => {});

// A request is worth retrying when it failed without a real HTTP response: a
// timeout (server slow / cold) or a network blip. A 4xx/5xx with a response is
// a real answer and must NOT be retried.
function isRetryableError(error) {
  if (error.code === 'ERR_CANCELED') return false;          // intentionally aborted
  if (error.response) return false;                          // server answered (even an error)
  const isTimeout = error.code === 'ECONNABORTED' || /timeout/i.test(error.message || '');
  return isTimeout || error.code === 'ERR_NETWORK' || !error.response;
}

// Only auto-retry side-effect-free methods so a retry can never double-submit
// (e.g. create a booking / charge a card twice).
function isIdempotent(config) {
  const method = (config?.method || 'get').toLowerCase();
  return method === 'get' || method === 'head' || method === 'options';
}

const delay = (ms) => new Promise((r) => setTimeout(r, ms));

// ---- Single-flight refresh -------------------------------------------------
// When the access token expires, several requests can 401 at once. Without
// coordination each one fires its own refresh (a 401 storm). Instead, the first
// 401 performs the refresh while the rest queue and replay once it resolves.
let isRefreshing = false;
let refreshWaiters = [];
let sessionExpiredFired = false;

function flushWaiters(newToken) {
  refreshWaiters.forEach((cb) => cb(newToken));
  refreshWaiters = [];
}

function handleSessionExpired() {
  // Idempotent: only react to the first unrecoverable 401.
  if (sessionExpiredFired) return;
  sessionExpiredFired = true;
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  try {
    useAuthStore.getState().triggerSessionExpired();
  } catch {
    // Store not ready (e.g. very early boot) - fall back to a hard redirect.
    window.location.href = '/login';
  }
}

// Response interceptor to handle token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config || {};
    const status = error.response?.status;

    // --- Auto-retry transient timeouts / network blips on safe requests ------
    // The server didn't really fail - it was just slow or briefly unreachable.
    // Back off briefly and try again before ever surfacing an error.
    if (isRetryableError(error) && isIdempotent(originalRequest)) {
      originalRequest._retryCount = (originalRequest._retryCount || 0) + 1;
      if (originalRequest._retryCount <= MAX_RETRIES) {
        await delay(RETRY_BASE_DELAY_MS * originalRequest._retryCount);
        return apiClient(originalRequest);
      }
    }

    // Not a 401, already retried, or a credential endpoint -> bubble up normally.
    if (status !== 401 || originalRequest._retry || AUTH_BYPASS.test(originalRequest.url || '')) {
      return Promise.reject(error);
    }
    originalRequest._retry = true;

    const refreshToken = localStorage.getItem('refresh_token');
    const accessToken = localStorage.getItem('access_token');

    // Public browsing: a visitor who was never logged in has no tokens. A 401
    // here just means "this action needs an account" - it is NOT an expired
    // session. Reject quietly so the page can show its own login prompt on
    // action (the LoginPromptModal), instead of taking over with the global
    // session-timeout modal.
    if (!refreshToken && !accessToken) {
      return Promise.reject(error);
    }

    if (!refreshToken) {
      handleSessionExpired();
      return NEVER();
    }

    // A refresh is already in flight - queue this request and replay it when done.
    if (isRefreshing) {
      return new Promise((resolve) => {
        refreshWaiters.push((newToken) => {
          if (!newToken) { resolve(NEVER()); return; }
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          resolve(apiClient(originalRequest));
        });
      });
    }

    isRefreshing = true;
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {}, {
        headers: { Authorization: `Bearer ${refreshToken}` },
      });
      const { access_token } = response.data.tokens;
      localStorage.setItem('access_token', access_token);
      isRefreshing = false;
      flushWaiters(access_token);

      originalRequest.headers.Authorization = `Bearer ${access_token}`;
      return apiClient(originalRequest);
    } catch (refreshError) {
      isRefreshing = false;
      flushWaiters(null);       // release queued requests into the silent NEVER path
      handleSessionExpired();   // show the "session timed out" modal, surface nothing
      return NEVER();
    }
  }
);

export default apiClient;
